import { useMemo } from "react"

import { useEvent } from "shared/lib/useEvent"
import { useScreenSize } from "shared/lib/useScreenSize"
import { Action } from "shared/types/Action"

import { useProductPositionProductInformationQuery } from "entities/product-positions/graphql/queries.generated"
import { getProductType } from "entities/products/lib/getProductType"
import { useCachedProduct } from "entities/products/model/store/useCachedProduct"
import { useProductObjLoad } from "entities/products/model/useProductObjLoad"
import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"
import { useProductPositionActionHandlers } from "features/product-position-management/model/useProductPositionActionHandlers"
import { useProductPositionActions } from "features/product-position-management/model/useProductPositionActions"
import { ProductPositionFormTabType } from "features/product-position-management/types/ProductPositionFormTabType"
import { useProjectStore } from "features/project-management/model/useProjectStore"

import { useProductPositionTableRowPayload } from "../../../contexts/ProductPositionTableRowPayloadContext"
import { ProductPositionTableProductPositionRowProps } from "../ProductPositionTableProductPositionRow"
import { ProductPositionTableProductPositionRowConnectorProps } from "../ProductPositionTableProductPositionRowConnector"

export function useProductPositionTableProductPositionRowModel(
  props: ProductPositionTableProductPositionRowConnectorProps
): ProductPositionTableProductPositionRowProps & { actions: Action[] } {
  const { item } = props
  const { pendingProjectChildren, projectID, isReadonly } =
    useProductPositionTableRowPayload()
  useProductObjLoad(item.variantKey)
  const cachedProduct = useCachedProduct(item.variantKey)

  const isPending = pendingProjectChildren?.includes(item.id)
  const { data, loading: isLoading } =
    useProductPositionProductInformationQuery({
      variables: { id: item.id },
      skip: isPending
    })
  const productInformation = data?.productPositionProductInformation

  const { isDesktop } = useScreenSize()
  const { selectElement, enterSidebarEditing, setSidebarActiveTab } =
    useProjectStore(projectID ?? "")

  const { permissions } = useProjectPermissions({
    projectID: projectID ?? ""
  })

  const handlers = useProductPositionActionHandlers({
    projectID: projectID ?? "",
    productPosition: item,
    permissions: permissions ?? readonlyProjectPermissions
  })

  const handleEdit = useEvent(() => {
    if (isDesktop) {
      selectElement(item.id, { preventUnselectOnReselect: true })
      enterSidebarEditing()
    } else {
      handlers.onEdit?.()
    }
  })

  const handleAssignAttachments = useEvent(() => {
    if (isDesktop) {
      selectElement(item.id, { preventUnselectOnReselect: true })
      enterSidebarEditing()
      setSidebarActiveTab(ProductPositionFormTabType.ATTACHMENTS)
    } else {
      handlers.onAssignAttachments?.()
    }
  })

  const handleAddClarificationRequest = useEvent(() => {
    if (isDesktop) {
      selectElement(item.id, { preventUnselectOnReselect: true })
      enterSidebarEditing()
      setSidebarActiveTab(ProductPositionFormTabType.CLARIFICATION_REQUEST)
    } else {
      handlers.onAddClarificationRequest?.()
    }
  })

  const { actions } = useProductPositionActions({
    idPrefix: `product-position-row-${item.id}`,
    isReadonly,
    onEdit: handlers.onEdit ? handleEdit : undefined,
    onDuplicate: handlers.onDuplicate,
    onCopy: handlers.onCopy,
    onAssignAttachments: handlers.onAssignAttachments
      ? handleAssignAttachments
      : undefined,
    onAddClarificationRequest: handlers.onAddClarificationRequest
      ? handleAddClarificationRequest
      : undefined,
    onDelete: handlers.onDelete
  })

  const enhancedPayload = useMemo(
    () => ({
      productType: getProductType(item.variantKey),
      title: cachedProduct?.title,
      isTitleLoading: cachedProduct === undefined,
      isValid: !!productInformation,
      isLoadingValidity: isLoading,
      productInformation,
      isPending: !!isPending
    }),
    [productInformation, isLoading, cachedProduct, isPending]
  )

  return {
    ...props,
    ...enhancedPayload,
    actions
  }
}
